import type { PageHelp } from '../../help/types';

/** Page guide for `/packages` — the package catalogue and the "New package" dialog (spec §9.3, §9.5). */
export const packagesHelp: PageHelp = {
  title: 'Packages',
  intro:
    'Keys shared between applications, each owned by exactly one package. Every subscribing project exports a package’s keys as its own, so this list shows how far a change here would reach.',
  sections: ['The package list', 'Creating a package'],
  contexts: {
    'create-dialog': 'Click “New package” at the top right (administrators only).',
  },
  tips: [
    'A package key edited from a project by someone without the owner role on the package is not written: it becomes a suggestion that a package owner accepts or rejects. On the package’s own Keys page, owners and translators of the package write directly.',
    'Subscriptions are made from the project side: a project owner picks the packages in that project’s settings.',
  ],
  steps: [
    {
      selector: '[data-help="packages-list"]',
      title: 'Read the package list',
      body: 'Every registered user sees every package. Prefixes are the first key-name segments the package owns, Keys counts its keys, and Subscribers is the number of projects a change here would reach. Your roles shows your grants on the package, “read only” when you have none; an administrator sees “admin” there and may change every package.',
      section: 'The package list',
      i18n: {
        'de-AT': {
          title: 'Paketliste lesen',
          body: 'Jede registrierte Person sieht alle Pakete. Prefixes sind die ersten Segmente der Schlüsselnamen, die dem Paket gehören, Keys zählt seine Schlüssel, und Subscribers ist die Zahl der Projekte, die eine Änderung hier erreicht. Your roles zeigt Ihre Berechtigungen für das Paket, „read only“, wenn Sie keine haben; Administratoren sehen dort „admin“ und dürfen jedes Paket ändern.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje liste paketa',
          body: 'Svaki registrovani korisnik vidi sve pakete. Prefixes su prvi segmenti imena ključeva koje paket poseduje, Keys broji njegove ključeve, a Subscribers je broj projekata do kojih bi izmena ovde stigla. Your roles prikazuje vaša prava na paketu, a „read only“ kada ih nemate; administrator tu vidi „admin“ i sme da menja svaki paket.',
        },
      },
    },
    {
      selector: '[data-help="packages-keys"]',
      title: 'Open the package keys',
      body: 'Opens the editor on this package’s own keys: create keys with the package’s prefixes, translate, tag, run QA and AI. Everyone can look; owners and translators of the package edit values there directly, owners and developers manage keys.',
      section: 'The package list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Paketschlüssel öffnen',
          body: 'Öffnet den Editor für die eigenen Schlüssel dieses Pakets: Schlüssel mit den Präfixen des Pakets anlegen, übersetzen, taggen, QA und KI ausführen. Ansehen kann jede Person; Owner und Translator des Pakets bearbeiten dort Werte direkt, Owner und Developer verwalten Schlüssel.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje ključeva paketa',
          body: 'Otvara editor nad sopstvenim ključevima ovog paketa: kreiranje ključeva sa prefiksima paketa, prevođenje, tagovi, QA i AI. Pogledati može svako; owner i translator paketa tu direktno menjaju vrednosti, a owner i developer upravljaju ključevima.',
        },
      },
    },
    {
      selector: '[data-help="packages-imports"]',
      title: 'Open the package imports',
      body: 'Opens the upload of simple_json files straight into this package, with the history of earlier uploads and their revert. A file’s keys must carry the package’s prefixes.',
      section: 'The package list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Paket-Importe öffnen',
          body: 'Öffnet den Upload von simple_json-Dateien direkt in dieses Paket, mit der Historie früherer Uploads und deren Rücknahme. Die Schlüssel einer Datei müssen die Präfixe des Pakets tragen.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje uvoza u paket',
          body: 'Otvara otpremanje simple_json datoteka direktno u ovaj paket, sa istorijom ranijih otpremanja i njihovim poništavanjem. Ključevi u datoteci moraju imati prefikse paketa.',
        },
      },
    },
    {
      selector: '[data-help="packages-suggestions"]',
      title: 'Open the suggestions inbox',
      body: 'Opens the wording that projects have proposed for this package’s keys. Anyone may read the proposals; accepting or rejecting one needs the owner role on the package or an administrator.',
      section: 'The package list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Vorschläge öffnen',
          body: 'Öffnet die Formulierungen, die Projekte für die Schlüssel dieses Pakets vorgeschlagen haben. Lesen darf sie jede Person; annehmen oder ablehnen dürfen nur Personen mit der Rolle Owner im Paket oder Administratoren.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje predloga',
          body: 'Otvara formulacije koje su projekti predložili za ključeve ovog paketa. Predloge može da čita svako; za prihvatanje ili odbijanje potrebna je uloga owner na paketu ili administratorska prava.',
        },
      },
    },
    {
      selector: '[data-help="packages-settings"]',
      title: 'Open package settings',
      body: 'Opens the name, key prefixes, locales, subscribers and coverage of this package. Everyone can look; only a package owner or an administrator can change anything there.',
      section: 'The package list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Paketeinstellungen öffnen',
          body: 'Öffnet Name, Schlüsselpräfixe, Sprachen, Abonnenten und Abdeckung dieses Pakets. Ansehen kann sie jede Person; ändern können dort nur Paket-Owner oder Administratoren.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje podešavanja paketa',
          body: 'Otvara naziv, prefikse ključeva, jezike, pretplatnike i pokrivenost ovog paketa. Pogledati može svako; menjati može samo owner paketa ili administrator.',
        },
      },
    },
    {
      selector: '[data-help="packages-create"]',
      title: 'Create a package',
      body: 'Opens a dialog for the code, name, key prefixes and source locale of a new package. The prefixes are the point: they say which key names this package owns, and a prefix another project or package already holds is refused rather than shared.',
      section: 'Creating a package',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Paket anlegen',
          body: 'Öffnet einen Dialog für Code, Namen, Schlüsselpräfixe und Ausgangssprache eines neuen Pakets. Entscheidend sind die Präfixe: Sie legen fest, welche Schlüsselnamen diesem Paket gehören, und ein Präfix, das bereits ein anderes Projekt oder Paket hält, wird abgelehnt statt geteilt.',
        },
        'sr-Latn-RS': {
          title: 'Kreiranje paketa',
          body: 'Otvara dijalog za kod, naziv, prefikse ključeva i izvorni jezik novog paketa. Prefiksi su suština: oni određuju koja imena ključeva pripadaju ovom paketu, a prefiks koji već drži drugi projekat ili paket biva odbijen, ne deli se.',
        },
      },
    },
    {
      selector: '[data-help="packages-create-code"]',
      title: 'Choose the code',
      body: 'A lower-case letter followed by letters, digits or dashes, 2–40 characters in total, for example `common-taxonomy`. It appears in the package’s URLs, must be unique and cannot be changed later. Unlike a project code, it is not a key prefix.',
      section: 'Creating a package',
      context: 'create-dialog',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Code wählen',
          body: 'Ein Kleinbuchstabe, gefolgt von Buchstaben, Ziffern oder Bindestrichen, insgesamt 2–40 Zeichen, zum Beispiel `common-taxonomy`. Er erscheint in den URLs des Pakets, muss eindeutig sein und lässt sich später nicht ändern. Anders als ein Projektcode ist er kein Schlüsselpräfix.',
        },
        'sr-Latn-RS': {
          title: 'Izbor koda',
          body: 'Malo slovo za kojim slede slova, cifre ili crtice, ukupno 2–40 znakova, na primer `common-taxonomy`. Pojavljuje se u URL-ovima paketa, mora biti jedinstven i kasnije se ne može promeniti. Za razliku od koda projekta, nije prefiks ključeva.',
        },
      },
    },
    {
      selector: '[data-help="packages-create-name"]',
      title: 'Name the package',
      body: 'The display name shown in this list and as the title of the package’s pages. Required, up to 100 characters, and it can be changed later in package settings.',
      section: 'Creating a package',
      context: 'create-dialog',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Paket benennen',
          body: 'Der Anzeigename in dieser Liste und als Titel der Paketseiten. Pflichtfeld, bis zu 100 Zeichen, und später in den Paketeinstellungen änderbar.',
        },
        'sr-Latn-RS': {
          title: 'Imenovanje paketa',
          body: 'Naziv koji se prikazuje u ovoj listi i kao naslov stranica paketa. Obavezan je, do 100 znakova, i kasnije se može promeniti u podešavanjima paketa.',
        },
      },
    },
    {
      selector: '[data-help="packages-create-prefixes"]',
      title: 'List the key prefixes',
      body: 'Comma-separated first segments of key names, for example `Common, Country`; the chips below the fields show how the list was read. At least one is required, each a single segment of letters, digits, underscores or dashes with no dot. A prefix another project or package holds is refused.',
      section: 'Creating a package',
      context: 'create-dialog',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Schlüsselpräfixe angeben',
          body: 'Durch Beistriche getrennte erste Segmente von Schlüsselnamen, zum Beispiel `Common, Country`; die Chips unter den Feldern zeigen, wie die Liste gelesen wurde. Mindestens eines ist Pflicht, jedes ein einzelnes Segment aus Buchstaben, Ziffern, Unterstrichen oder Bindestrichen ohne Punkt. Ein Präfix, das ein anderes Projekt oder Paket hält, wird abgelehnt.',
        },
        'sr-Latn-RS': {
          title: 'Navođenje prefiksa ključeva',
          body: 'Prvi segmenti imena ključeva odvojeni zarezom, na primer `Common, Country`; čipovi ispod polja pokazuju kako je lista pročitana. Obavezan je bar jedan, svaki kao jedan segment od slova, cifara, donjih crta ili crtica, bez tačke. Prefiks koji drži drugi projekat ili paket biva odbijen.',
        },
      },
    },
    {
      selector: '[data-help="packages-create-source-locale"]',
      title: 'Pick the source locale',
      body: 'The language the package’s keys are written in; subscribing projects translate from it. A new package carries only this locale, so add the others in package settings afterwards.',
      section: 'Creating a package',
      context: 'create-dialog',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Ausgangssprache wählen',
          body: 'Die Sprache, in der die Schlüssel des Pakets geschrieben sind; abonnierende Projekte übersetzen aus ihr. Ein neues Paket führt nur diese Sprache, fügen Sie die übrigen danach in den Paketeinstellungen hinzu.',
        },
        'sr-Latn-RS': {
          title: 'Izbor izvornog jezika',
          body: 'Jezik na kome su napisani ključevi paketa; projekti pretplatnici prevode sa njega. Novi paket ima samo ovaj jezik, pa ostale dodajte posle toga u podešavanjima paketa.',
        },
      },
    },
    {
      selector: '[data-help="packages-create-submit"]',
      title: 'Create the package',
      body: 'Saves the package and closes the dialog. A field that fails validation says why below it; when the server refuses a value, such as a code or a prefix that is already taken, the reason is shown in the dialog and nothing is created. Cancel closes the dialog and drops that message.',
      section: 'Creating a package',
      context: 'create-dialog',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Paket erstellen',
          body: 'Speichert das Paket und schließt den Dialog. Ein Feld, das die Prüfung nicht besteht, nennt den Grund darunter; lehnt der Server einen Wert ab, etwa einen bereits vergebenen Code oder ein vergebenes Präfix, steht der Grund im Dialog und es wird nichts angelegt. „Cancel“ schließt den Dialog und verwirft diese Meldung.',
        },
        'sr-Latn-RS': {
          title: 'Kreiranje paketa',
          body: 'Čuva paket i zatvara dijalog. Polje koje ne prođe proveru ispod sebe navodi razlog; kada server odbije neku vrednost, na primer kod ili prefiks koji je već zauzet, razlog se prikazuje u dijalogu i ništa se ne kreira. „Cancel“ zatvara dijalog i odbacuje tu poruku.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Pakete',
      intro:
        'Schlüssel, die mehrere Anwendungen gemeinsam nutzen, jeder im Besitz genau eines Pakets. Jedes abonnierende Projekt exportiert die Schlüssel eines Pakets wie seine eigenen; diese Liste zeigt daher, wie weit eine Änderung hier reicht.',
      sections: {
        'The package list': 'Die Paketliste',
        'Creating a package': 'Paket anlegen',
      },
      contexts: {
        'create-dialog': 'Klicken Sie rechts oben auf „New package“ (nur Administratoren).',
      },
      tips: [
        'Bearbeitet jemand ohne die Rolle Owner im Paket einen Paketschlüssel aus einem Projekt heraus, wird nichts geschrieben: Es entsteht ein Vorschlag, den ein Paket-Owner annimmt oder ablehnt. Auf der eigenen Schlüsselseite des Pakets schreiben Owner und Translator des Pakets direkt.',
        'Abonnements werden auf der Projektseite angelegt: Ein Projekt-Owner wählt die Pakete in den Einstellungen des Projekts.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Paketi',
      intro:
        'Ključevi koje deli više aplikacija, a svaki pripada tačno jednom paketu. Svaki projekat pretplatnik izvozi ključeve paketa kao svoje, pa ova lista pokazuje dokle bi izmena ovde stigla.',
      sections: {
        'The package list': 'Lista paketa',
        'Creating a package': 'Kreiranje paketa',
      },
      contexts: {
        'create-dialog': 'Kliknite na „New package“ gore desno (samo administratori).',
      },
      tips: [
        'Kada neko bez uloge owner na paketu izmeni ključ paketa iz projekta, ništa se ne upisuje: nastaje predlog koji owner paketa prihvata ili odbija. Na sopstvenoj stranici Keys paketa owner i translator paketa upisuju direktno.',
        'Pretplate se prave sa strane projekta: owner projekta bira pakete u podešavanjima tog projekta.',
      ],
    },
  },
};
